import { API_BASE_URL } from '../config/serverApiConfig';
import { api } from './api';

export interface WorkspaceMember {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  enabled: boolean;
  emailVerified: boolean;
  roles: string[];
  role?: string | null;
  custom_role_id?: number | null;
}

const headers = (workspaceId: number) => ({
  'X-Workspace-Id': String(workspaceId)
});

export const listWorkspaceMembers = async (workspaceId: number, q?: string) => {
  const response = await api().get(`${API_BASE_URL}workspace/users`, {
    headers: headers(workspaceId),
    params: { workspace_id: workspaceId, ...(q ? { q } : {}) },
    cache: false
  } as any);
  return (response.data?.users ?? []) as WorkspaceMember[];
};

/** Somebody who reaches the workspace through a descending role on a node above it. */
export interface InheritedMember {
  user_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  role: string;
  source_node_id: number | null;
  /** Null when the caller cannot reach the node that grants the access. */
  source_node_name: string | null;
  source_node_kind: 'organisation' | 'entity' | 'workspace';
}

export const listInheritedMembers = async (workspaceId: number) => {
  const response = await api().get(`${API_BASE_URL}workspace/members/inherited`, {
    headers: headers(workspaceId),
    cache: false
  } as any);
  return (response.data?.members ?? []) as InheritedMember[];
};

/** People who reach an entity through a descending role on the organisation above it. */
export const listEntityInheritedMembers = async (entityId: number) => {
  const response = await api().get(`${API_BASE_URL}r/v2/entities/${entityId}/members/inherited`, {
    cache: false
  } as any);
  return (response.data?.members ?? []) as InheritedMember[];
};

export const addWorkspaceMember = async (workspaceId: number, body: { user_id: string; role?: string; custom_role_id?: number }) =>
  (await api().post(`${API_BASE_URL}workspace/members`, body, {
    headers: headers(workspaceId),
    params: { workspace_id: workspaceId }
  })).data;

export const updateWorkspaceMemberRole = async (workspaceId: number, userId: string, body: { role?: string; custom_role_id?: number }) =>
  (await api().put(`${API_BASE_URL}user/edit-roles`, body, {
    headers: { ...headers(workspaceId), 'X-Keycloak-User-Id': userId },
    params: { workspace_id: workspaceId }
  })).data;

export const removeWorkspaceMember = async (workspaceId: number, userId: string) =>
  (await api().delete(`${API_BASE_URL}workspace/users/${userId}`, {
    headers: headers(workspaceId),
    params: { workspace_id: workspaceId }
  })).data;

export const effectiveWorkspaceRole = async (workspaceId: number, role?: string | null, customRoleId?: number | null) => {
  const response = await api().get(`${API_BASE_URL}workspace/roles/effective`, {
    headers: headers(workspaceId),
    params: {
      workspace_id: workspaceId,
      ...(role ? { role } : {}),
      ...(customRoleId ? { custom_role_id: customRoleId } : {})
    },
    cache: false
  } as any);
  return response.data?.capabilities ?? {};
};
